const delimiterRegex = /\s/;
const quoteCharRegex = /['"]/;
const scriptRegex = /<script([^>]*)>(.*)<\/script>/;
const allScriptsRegex = /<script([^>]*)>(.*?)<\/script>/g;

const invalidResult = {
  attrs: {},
  content: '',
  valid: false,
};

/**
 * Утилита ранее не учитывала ситуации, когда на вход поступал сниппет
 * с несколькими тегами `script` внутри.
 *
 * В качестве результата возвращает массив объектов { valid, attrs, content }
 * Тест-кейсы были обновлены
 *
 * См. в `script-parser.spec.js`.
 */

export default function parseAllScripts(str) {
  const scripts = str
    .match(allScriptsRegex);

  if (!scripts) return [invalidResult];

  return scripts
    .map(parseSingleScriptString);
}

/**
 * Утилита для парсинга строки с тегом `script`.
 *
 * Написана так как отдельных парсеров для тегов `script` нет. Есть либо
 * полноценные решения по работе с `html` которые весят не позволительно
 * медленно, либо регулярки которые не покрывают всех правил резолвинга
 * значений.
 *
 * @author Anton Ignatov <a.ignatov@rambler-co.ru>
 */
function parseSingleScriptString(str) {
  const match = str.match(scriptRegex);

  if (!match) return invalidResult;

  const attrs = {};
  const [, attrsString, content] = match;

  /**
   * Стек символов относящийся к атрибуту и его значению.
   */
  const stack = [];

  /**
   * Флаг обозначающий начали ли мы работать с именем атрибута.
   */
  let key = false;

  /**
   * Флаг обозначает, что атрибут имеет пустое значение.
   * Например: `<script defer></script>`.
   *
   * Такие атрибуты требуют специальной обработки.
   */
  let empty = false;

  /**
   * Флаг обозначает, что мы обработали открывающую ковычку и дальше будет
   * часть содержащая имя атрибута (потому что курсор двигается с конца
   * в начало).
   */
  let opened = false;

  /**
   * Флаг обозначает, что мы обработали закрывающую ковычку и дальше будет
   * значение (потому что курсор двигается с конца в начало).
   */
  let closed = false;

  /**
   * Флаг обозначает, что мы обработали символ `=` и дальше начнется
   * непосредственно имя атрибута.
   */
  let entered = false;

  /**
   * Символ используемый для обрамления значения атрибута. Открывающий
   * и закрывающий символ должны совпадать иначе это будет не валидное
   * значение.
   */
  let quoteChar = null;

  // Позиция курсора.
  let i = attrsString.length - 1;

  /**
   * Обход строки с атрибутами проще делать с конца. Это позволит проще
   * определять начало и конец атрибута и его значение. Так же это позволит
   * легко реализовать приоритет значения если на теге будет два атрибута
   * с одинаковым значением. В данном случае первое значение имеет приоритет.
   */
  while (i >= 0) {
    const char = attrsString[i];

    if (stack.length) {
      if (opened && closed) {
        const isQuote = quoteCharRegex.test(char);
        const isDelimiter = delimiterRegex.test(char);

        if (isQuote && !key && !empty) return invalidResult;
        if (!isQuote && !isDelimiter && entered) key = true;
        if (char === '=') entered = true;

        /**
         * Если мы обрабатываем часть содержащую имя атрибута, то мы должны
         * игнорировать пробелы. Имя их не может содержать.
         */
        if (isDelimiter && !key && !empty) {
          i--;
          continue;
        }

        const valid = !(isQuote || isDelimiter);

        if (valid) stack.push(char);

        if (!i || !valid) {
          const [name, ...valueParts] = stack
            .reverse()
            .join('')
            .split('=');

          attrs[name] = valueParts.join('=').slice(1, -1);

          // Очищаем стек и сбрасываем все переменные в изначальные значения.
          stack.length = 0;
          key = false;
          empty = false;
          opened = false;
          closed = false;
          entered = false;
          quoteChar = null;

          /**
           * Пропускаем оставшуюся часть с перемещением курсора чтоб
           * еще раз обработать символ так как он может быть началом нового
           * атрибута.
           */
          continue;
        }
      } else {
        if (char === quoteChar) opened = true;
        stack.push(char);
      }
    } else if (quoteCharRegex.test(char)) {
      closed = true;
      quoteChar = char;
      stack.push(char);
    } else if (!delimiterRegex.test(char)) {
      empty = true;
      opened = true;
      closed = true;
      stack.push(char);
    }

    i--;
  }

  return {
    attrs,
    content,
    valid: true,
  };
}
